import React, { useState, useEffect } from 'react';
import { myAxios } from '../services/Helper';
import './ManageSubFunctionItems.css';

const ManageSubFunctionItems = () => {
    const [functions, setFunctions] = useState([]);
    const [subFunctions, setSubFunctions] = useState([]);
    const [selectedFunction, setSelectedFunction] = useState('');
    const [selectedSubFunction, setSelectedSubFunction] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [itemData, setItemData] = useState({
        name: '',
        description: '',
        price: ''
    });
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Fetch functions on component mount
    useEffect(() => {
        const fetchFunctions = async () => {
            try {
                const response = await myAxios.get('/function');
                console.log('Functions response:', response.data);
                setFunctions(response.data.map(func => ({
                    id: func.functionId,
                    name: func.functionName
                })));
                setLoading(false);
            } catch (error) {
                console.error('Error fetching functions:', error);
                setError('Error fetching functions: ' + (error.response?.data?.message || error.message));
                setLoading(false);
            }
        };
        fetchFunctions();
    }, []);

    // Fetch subfunctions when a function is selected
    useEffect(() => {
        const fetchSubFunctions = async () => {
            if (selectedFunction) {
                try {
                    setLoading(true);
                    const response = await myAxios.get(`/subfunction/getByFunctionID/${selectedFunction}`);
                    console.log('Subfunctions response:', response.data);
                    setSubFunctions(response.data.map(sub => ({
                        id: sub.subfunctionId,
                        name: sub.subFunctionName
                    })));
                    setLoading(false);
                } catch (error) {
                    console.error('Error fetching subfunctions:', error);
                    setError('Error fetching subfunctions: ' + (error.response?.data?.message || error.message));
                    setLoading(false);
                }
            } else {
                setSubFunctions([]);
            }
        };
        fetchSubFunctions();
    }, [selectedFunction]);

    // Fetch items when a subfunction is selected
    useEffect(() => {
        const fetchItems = async () => {
            if (selectedSubFunction) {
                try {
                    setLoading(true);
                    console.log('Fetching items for subfunction ID:', selectedSubFunction);
                    const response = await myAxios.get(`/api/SubfunctionCategory/items/bySubFunction1/${selectedSubFunction}`);
                    console.log('Items response:', response.data);

                    if (Array.isArray(response.data)) {
                        const mappedItems = response.data.map(item => ({
                            id: item.itemId,
                            name: item.itemName,
                            description: item.itemDesc,
                            price: parseFloat(item.itemPrice)
                        }));
                        console.log('Mapped items:', mappedItems);
                        setItems(mappedItems);
                    } else {
                        console.error('Expected array but got:', typeof response.data);
                        setItems([]);
                    }
                    setLoading(false);
                } catch (error) {
                    console.error('Error fetching items:', error);
                    setError('Error fetching items: ' + (error.response?.data?.message || error.message));
                    setLoading(false);
                }
            } else {
                setItems([]);
            }
        };
        fetchItems();
    }, [selectedSubFunction]);

    const handleFunctionChange = (e) => {
        setSelectedFunction(e.target.value);
        setSelectedSubFunction('');
        setItems([]);
    };

    const handleSubFunctionChange = (e) => {
        setSelectedSubFunction(e.target.value);
    };

    const handleAddItemClick = () => {
        if (selectedSubFunction) {
            setShowForm(true);
            setItemData({
                name: '',
                description: '',
                price: ''
            });
        } else {
            alert('Please select a sub-function first.');
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setItemData({ ...itemData, [name]: value });
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();

        if (!selectedSubFunction) {
            alert('Please select a sub-function first.');
            return;
        }

        setIsSubmitting(true);

        try {
            const newItem = {
                item_name: itemData.name,
                description: itemData.description,
                price: parseInt(itemData.price),
                subfunctionId: parseInt(selectedSubFunction)
            };

            console.log('Submitting new item:', newItem);

            // Using the correct API endpoint for creating items
            const response = await myAxios.post('/api/subfunctionitem/addSubfunctionItem', newItem);
            console.log('Item creation response:', response.data);

            // Fetch the updated items list
            const updatedItemsResponse = await myAxios.get(`/api/SubfunctionCategory/items/bySubFunction1/${selectedSubFunction}`);

            if (Array.isArray(updatedItemsResponse.data)) {
                const mappedItems = updatedItemsResponse.data.map(item => ({
                    id: item.itemId,
                    name: item.itemName,
                    description: item.itemDesc,
                    price: parseFloat(item.itemPrice)
                }));
                setItems(mappedItems);
            }

            // Reset form state
            setItemData({ name: '', description: '', price: '' });
            setShowForm(false);
            setError(null);

        } catch (error) {
            console.error('Error adding item:', error);
            setError('Error adding item: ' + (error.response?.data?.message || error.message));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        const confirmed = window.confirm("Are you sure you want to delete this item?");
        if (confirmed) {
            try {
                setLoading(true);
                await myAxios.delete(`/api/subfunctionitem/subfunctionitem/${id}`);
                setItems(items.filter(item => item.id !== id));
                setError(null);
                setLoading(false);
            } catch (error) {
                console.error('Error deleting item:', error);
                setError('Error deleting item: ' + (error.response?.data?.message || error.message));
                setLoading(false);
            }
        }
    };

    const handleEdit = (id) => {
        const itemToEdit = items.find(item => item.id === id);
        if (itemToEdit) {
            setItemData({
                id: itemToEdit.id,
                name: itemToEdit.name,
                description: itemToEdit.description,
                price: itemToEdit.price.toString()
            });
            setShowForm(true);
        }
    };

    const handleCancel = () => {
        setShowForm(false);
        setItemData({ name: '', description: '', price: '' });
    };

    return (
        <div className="functions-container">
            <div className="functions-header">
                <h2>Items Management</h2>
                {error && <div className="error-message">{error}</div>}
            </div>

            <div className="dropdowns">
                <select value={selectedFunction} onChange={handleFunctionChange}>
                    <option value="">Select Function</option>
                    {functions.map((func) => (
                        <option key={func.id} value={func.id}>
                            {func.name}
                        </option>
                    ))}
                </select>

                <select
                    value={selectedSubFunction}
                    onChange={handleSubFunctionChange}
                    disabled={!selectedFunction || loading}
                >
                    <option value="">Select Sub-Function</option>
                    {subFunctions.map((sub) => (
                        <option key={sub.id} value={sub.id}>
                            {sub.name}
                        </option>
                    ))}
                </select>

                <button
                    className="add-item-btn"
                    onClick={handleAddItemClick}
                    disabled={!selectedSubFunction || loading}
                >
                    Add Item
                </button>
            </div>

            {loading ? (
                <div className="loading-message">Loading...</div>
            ) : (
                <div className="items-list">
                    {!selectedSubFunction ? (
                        <div className="no-items-message">Please select a sub-function to view items</div>
                    ) : items.length === 0 ? (
                        <div className="no-items-message">
                            No items found for this sub-function. Click "Add Item" to create one.
                        </div>
                    ) : (
                        items.map((item) => (
                            <div key={item.id} className="item-card">
                                <h4>{item.name || 'Untitled Item'}</h4>
                                <p className="description">{item.description || 'No description available'}</p>
                                <div className="price">₹{item.price?.toLocaleString() || '0'}</div>
                                <div className="card-buttons">
                                    <button className="edit-btn" onClick={() => handleEdit(item.id)}>Edit</button>
                                    <button className="delete-btn" onClick={() => handleDelete(item.id)}>Delete</button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}

            {showForm && (
                <div className="form-overlay">
                    <div className="form-container">
                        <h3>{itemData.id ? 'Edit Item' : 'Add New Item'}</h3>
                        <form onSubmit={handleFormSubmit}>
                            <input
                                type="text"
                                name="name"
                                placeholder="Item Name"
                                value={itemData.name}
                                onChange={handleInputChange}
                                required
                            />
                            <textarea
                                name="description"
                                placeholder="Item Description"
                                value={itemData.description}
                                onChange={handleInputChange}
                                required
                            ></textarea>
                            <input
                                type="number"
                                name="price"
                                placeholder="Price"
                                value={itemData.price}
                                onChange={handleInputChange}
                                required
                            />
                            <div className="form-buttons">
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? 'Saving...' : (itemData.id ? 'Update' : 'Add')}
                                </button>
                                <button
                                    type="button"
                                    onClick={handleCancel}
                                    disabled={isSubmitting}
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManageSubFunctionItems;